import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Send the signed-in user's API token with every request.
// Read from localStorage directly to avoid a circular import with useAuth.
apiClient.interceptors.request.use((config) => {
  try {
    const token = JSON.parse(localStorage.getItem('currentUser') || 'null')?.token;
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // ignore malformed stored user
  }
  return config;
});

// If the API says the token is missing/expired, sign out and go to sign-in.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && error.response?.data?.code === 'auth_required') {
      const { setCurrentUser } = await import('../hooks/useAuth');
      setCurrentUser(null);
      if (!window.location.pathname.startsWith('/signin')) {
        window.location.assign('/signin');
      }
    }
    return Promise.reject(error);
  }
);

export interface Profile {
  name: string;
  profession: string;
  interests: string[];
  location: string;
  personalityTraits: string[];
  favoriteColor: string;
  preferences: Preference[];
}

export interface Preference {
  category: string;
  name: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  tags: string[];
}

export interface Campaign {
  name: string;
  setting: string;
  tone: string;
  themes: string[];
  factions: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Goal {
  id: string;
  goal: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed' | 'planning';
  deadline?: string;
  timeframe?: string;
  createdAt: string;
}

export interface Goals {
  shortTerm: Goal[];
  longTerm: Goal[];
  philosophy: string;
}

export interface Projects {
  activeProjects: ActiveProject[];
  pastProjects: PastProject[];
}

export interface BaseProject {
  id: string;
  name: string;
  description: string;
  technologies: string[];
  status: string;
  category: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ActiveProject extends BaseProject {
  priority: 'high' | 'medium' | 'low';
  startDate?: string;
  targetDate?: string;
  progress: number;
  repository?: string;
}

export interface PastProject extends BaseProject {
  completedDate?: string;
  duration?: string;
  outcome?: string;
}

export interface Project extends BaseProject {
  // For backward compatibility
  priority?: 'high' | 'medium' | 'low';
  startDate?: string;
  targetDate?: string;
  progress?: number;
  repository?: string;
  completedDate?: string;
  duration?: string;
  outcome?: string;
}

export interface Values {
  values: string[];
  currentFocus: string;
}

export interface Relationships {
  collaborators: Collaborator[];
  mentors: Mentor[];
  trustedPeers: string[];
}

export interface Collaborator {
  name: string;
  relationshipType: string;
  notes: string;
}

export interface Mentor {
  name: string;
  influence: string;
}

export interface Place {
  id: number;
  name: string;
  city: string;
  country: string;
  latitude?: number;
  longitude?: number;
  createdAt: string;
  updatedAt: string;
}

export interface PersonalMetrics {
  id: string;
  date: string;
  timestamp?: string;
  steps?: number;
  weight?: {
    value: number;
    unit: 'lbs' | 'kg';
  };
  heartRate?: {
    resting?: number;
    max?: number;
    average?: number;
  };
  sleep?: {
    duration: number;
    quality: 'poor' | 'fair' | 'good' | 'excellent';
  };
  calories?: {
    burned?: number;
    consumed?: number;
  };
  bloodPressure?: {
    systolic: number;
    diastolic: number;
  };
  hydration?: {
    amount: number;
    unit: 'oz' | 'ml' | 'liters';
  };
}

export interface BodyMeasurement {
  id: string;
  date: string;
  waist?: { value: number; unit: string };
  chest?: { value: number; unit: string };
  hips?: { value: number; unit: string };
  neck?: { value: number; unit: string };
  arms?: {
    bicep?: { value: number; unit: string };
    forearm?: { value: number; unit: string };
  };
  legs?: {
    thigh?: { value: number; unit: string };
    calf?: { value: number; unit: string };
    inseam?: { value: number; unit: string };
  };
  bodyFatPercentage?: number;
  muscleMass?: { value: number; unit: string };
  notes?: string;
}

export interface NutritionEntry {
  id: string;
  date: string;
  timestamp: string;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  foods: FoodItem[];
  totalCalories: number;
  totalMacros: MacroNutrients;
  waterIntake?: { amount: number; unit: string };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FoodItem {
  name: string;
  quantity: number;
  unit: string;
  calories: number;
  macros: MacroNutrients;
}

export interface MacroNutrients {
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
}

export interface Contact {
  id: string;
  firstName: string;
  lastName?: string;
  displayName?: string;
  category: 'family' | 'friend' | 'professional' | 'emergency' | 'business';
  emails?: Array<{
    email: string;
    type: 'personal' | 'work' | 'other';
    primary: boolean;
  }>;
  phones?: Array<{
    number: string;
    type: 'mobile' | 'home' | 'work' | 'fax' | 'other';
    primary: boolean;
  }>;
  addresses?: Array<{
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    type: 'home' | 'work' | 'other';
  }>;
  company?: string;
  jobTitle?: string;
  birthday?: string;
  notes?: string;
  socialMedia?: Array<{
    platform: string;
    handle: string;
    url: string;
  }>;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  color?: string;
  vin?: string;
  licensePlate?: string;
  mileage?: number;
  fuelType?: 'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'plug-in-hybrid';
  transmission?: 'automatic' | 'manual' | 'cvt';
  insurance?: {
    provider: string;
    policyNumber: string;
    expirationDate: string;
  };
  registration?: {
    state: string;
    expirationDate: string;
  };
  maintenance?: Array<{
    date: string;
    type: string;
    mileage: number;
    cost: number;
    notes?: string;
  }>;
  purchaseInfo?: {
    date: string;
    price: number;
    dealer?: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface HabitDefinition {
  id: string;
  name: string;
  description?: string;
  category: 'health' | 'productivity' | 'personal' | 'social' | 'learning';
  frequency: 'daily' | 'weekly' | 'monthly' | 'custom';
  targetValue?: number;
  unit?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface HabitEntry {
  id: string;
  habitId: string;
  date: string;
  completed: boolean;
  value?: number;
  unit?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Bookmark {
  id: string;
  title: string;
  url: string;
  description?: string;
  category: 'work' | 'personal' | 'learning' | 'entertainment' | 'reference' | 'shopping' | 'social' | 'other';
  tags?: string[];
  favicon?: string;
  isPrivate: boolean;
  isFavorite: boolean;
  visitCount?: number;
  lastVisited?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Blog {
  id: string;
  userId: string;
  title: string;
  description: string;
  slug: string;
  status: 'active' | 'inactive' | 'archived';
  theme: string;
  settings: {
    isPublic: boolean;
    allowComments: boolean;
    moderateComments: boolean;
    showAuthor: boolean;
    showDate: boolean;
    postsPerPage: number;
  };
  metadata: {
    category: string;
    tags: string[];
    language: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface BlogPost {
  id: string;
  blogId: string;
  userId: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  status: 'draft' | 'published' | 'scheduled' | 'archived';
  publishedAt: string | null;
  scheduledAt: string | null;
  featuredImage: string | null;
  tags: string[];
  metadata: {
    readingTime: number;
    wordCount: number;
    seoTitle: string | null;
    seoDescription: string | null;
    canonicalUrl: string | null;
  };
  settings: {
    allowComments: boolean;
    isFeatured: boolean;
    isPinned: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

export interface BlogStats {
  totalBlogs: number;
  activeBlogs: number;
  totalPosts: number;
  publishedPosts: number;
  draftPosts: number;
  scheduledPosts: number;
  featuredPosts: number;
}

export interface PublicUser {
  id: string;
  username: string;
  displayName: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  profile: {
    bio: string;
    location: string;
  };
  createdAt: string;
}

export interface PublicProfile {
  user: PublicUser;
  blogs: Blog[];
  blogPosts: (BlogPost & { blog: Blog })[];
  stats: {
    totalBlogs: number;
    totalPosts: number;
    publishedPosts: number;
  };
}

export interface UserPreferences {
  // Dashboard & Interface Preferences
  dashboard: {
    defaultView: 'overview' | 'goals' | 'projects' | 'blogs';
    theme: 'light' | 'dark' | 'auto';
    compactMode: boolean;
    showWelcomeMessage: boolean;
    showQuickStats: boolean;
    showRecentActivity: boolean;
    cardsPerRow: number;
    autoRefresh: boolean;
    refreshInterval: number;
  };
  
  // Privacy & Profile Preferences  
  privacy: {
    profileVisibility: 'public' | 'private';
    showEmail: boolean;
    showLocation: boolean;
    allowPublicBlogAccess: boolean;
    showLastActive: boolean;
  };
  
  // Content & Blogging Preferences
  content: {
    defaultBlogVisibility: 'public' | 'private';
    autoSaveDrafts: boolean;
    autoSaveInterval: number;
    showWordCount: boolean;
    enableMarkdownPreview: boolean;
    defaultPostStatus: 'draft' | 'published';
    allowComments: boolean;
    moderateComments: boolean;
  };
  
  // Notification Preferences
  notifications: {
    email: boolean;
    blogComments: boolean;
    systemUpdates: boolean;
    weeklyDigest: boolean;
  };
  
  // Data & Export Preferences
  data: {
    exportFormat: 'json' | 'csv';
    includeAnalytics: boolean;
    timezone: string;
    dateFormat: string;
    timeFormat: '12h' | '24h';
  };
}

export interface SitePreferences {
  general: {
    siteName: string;
    siteDescription: string;
    siteUrl: string;
    adminEmail: string;
    supportEmail: string;
    timezone: string;
    language: string;
    allowRegistration: boolean;
    requireEmailVerification: boolean;
    maintenanceMode: boolean;
    maintenanceMessage: string;
    logoUrl: string | null;
    faviconUrl: string | null;
  };
  appearance: {
    defaultTheme: 'light' | 'dark' | 'auto';
    allowUserThemes: boolean;
    availableThemes: string[];
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    fontFamily: string;
    borderRadius: string;
    compactMode: boolean;
  };
  security: {
    passwordMinLength: number;
    requireStrongPasswords: boolean;
    sessionTimeout: number;
    maxLoginAttempts: number;
    lockoutDuration: number;
    requireTwoFactor: boolean;
    allowedLoginMethods: string[];
    enableBruteForceProtection: boolean;
    enableRateLimiting: boolean;
    rateLimitWindow: number;
    rateLimitMaxRequests: number;
  };
  notifications: {
    enableEmailNotifications: boolean;
    enablePushNotifications: boolean;
    enableSMSNotifications: boolean;
    defaultNotificationPreferences: {
      email: boolean;
      push: boolean;
      sms: boolean;
      systemUpdates: boolean;
      weeklyDigest: boolean;
    };
    emailProvider: string;
    smtpSettings: {
      host: string;
      port: number;
      secure: boolean;
      username: string;
      password: string;
    };
  };
  content: {
    allowPublicProfiles: boolean;
    allowUserBlogs: boolean;
    allowComments: boolean;
    moderateComments: boolean;
    allowFileUploads: boolean;
    maxFileSize: number;
    allowedFileTypes: string[];
    enableMarkdown: boolean;
    enableCodeHighlighting: boolean;
    defaultPostStatus: 'draft' | 'published';
    autoSaveDrafts: boolean;
    autoSaveInterval: number;
  };
  features: {
    enableBlogs: boolean;
    enableProjects: boolean;
    enableGoals: boolean;
    enableHabits: boolean;
    enableContacts: boolean;
    enableVehicles: boolean;
    enableBookmarks: boolean;
    enableMetrics: boolean;
    enableCampaigns: boolean;
    enableUserProfiles: boolean;
    enableSearch: boolean;
    enableTags: boolean;
    enableCategories: boolean;
  };
  lastUpdated: string;
  version: string;
}

export interface PreferencesSummary {
  site: {
    name: string;
    theme: string;
    registrationOpen: boolean;
    maintenanceMode: boolean;
    featuresEnabled: number;
  };
  user: {
    theme: string;
    language: string;
    timezone: string;
    notificationsEnabled: boolean;
    profileVisibility: string;
  } | null;
}

export const profileApi = {
  getProfile: () => apiClient.get<Profile>('/identity/profile'),
  updateProfile: (profile: Partial<Profile>) => 
    apiClient.put<Profile>('/identity/profile', profile),
};

export const campaignsApi = {
  getCampaigns: () => apiClient.get<Campaign[]>('/b1/campaigns'),
  getQuickstoneCampaign: () => apiClient.get<Campaign>('/v1/campaigns/quickstone'),
  createCampaign: (campaign: Omit<Campaign, 'themes' | 'factions' | 'createdAt' | 'updatedAt'> & { themes?: string[]; factions?: string[] }) =>
    apiClient.post<Campaign>('/b1/campaigns', campaign),
  updateQuickstoneCampaign: (campaign: Partial<Campaign>) =>
    apiClient.put<Campaign>('/v1/campaigns/quickstone', campaign),
  deleteCampaign: (name: string) =>
    apiClient.delete(`/b1/campaigns/${name}`),
};

export const goalsApi = {
  getGoals: () => apiClient.get<Goals>('/cognition/goals'),
  updateGoals: (goals: Partial<Goals>) => 
    apiClient.put<Goals>('/cognition/goals', goals),
  addGoal: (goal: { 
    goal: string; 
    type: 'shortTerm' | 'longTerm'; 
    priority?: 'high' | 'medium' | 'low'; 
    deadline?: string; 
    timeframe?: string; 
    philosophy?: string;
    status?: string;
  }) => apiClient.post<Goals>('/cognition/goals', goal),
  deleteGoal: (id: string) => apiClient.delete(`/cognition/goals/${id}`),
};

export const projectsApi = {
  getProjects: () => apiClient.get<Projects>('/work/projects'),
  updateProjects: (projects: Partial<Projects>) => 
    apiClient.put<Projects>('/work/projects', projects),
  addProject: (project: {
    name: string;
    description: string;
    type: 'active' | 'past';
    technologies?: string[];
    status?: string;
    category?: string;
    priority?: 'high' | 'medium' | 'low';
    startDate?: string;
    targetDate?: string;
    progress?: number;
    repository?: string;
    completedDate?: string;
    duration?: string;
    outcome?: string;
  }) => apiClient.post<Projects>('/work/projects', project),
  deleteProject: (id: string) => apiClient.delete(`/work/projects/${id}`),
};

export const valuesApi = {
  getValues: () => apiClient.get<Values>('/cognition/values'),
  updateValues: (values: Partial<Values>) => 
    apiClient.put<Values>('/cognition/values', values),
};

export const relationshipsApi = {
  getRelationships: () => apiClient.get<Relationships>('/v1/relationships'),
  updateRelationships: (relationships: Partial<Relationships>) => 
    apiClient.put<Relationships>('/v1/relationships', relationships),
  addRelationship: (relationship: { name: string; relationshipType: string; notes?: string; influence?: string; type: 'collaborator' | 'mentor' | 'trustedPeer' }) =>
    apiClient.post<Relationships>('/v1/relationships', relationship),
  deleteRelationship: (id: string) => apiClient.delete(`/v1/relationships/${id}`),
};

export const placesApi = {
  getPlaces: () => apiClient.get<Place[]>('/v1/places'),
  getPlace: (id: number) => apiClient.get<Place>(`/v1/places/${id}`),
  createPlace: (place: Omit<Place, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiClient.post<Place>('/v1/places', place),
  updatePlace: (id: number, place: Partial<Place>) =>
    apiClient.put<Place>(`/v1/places/${id}`, place),
  deletePlace: (id: number) => apiClient.delete(`/v1/places/${id}`),
};

export const metricsApi = {
  getMetrics: (params?: { startDate?: string; endDate?: string; type?: string }) =>
    apiClient.get<PersonalMetrics[]>('/v1/metrics', { params }),
  addMetrics: (metrics: Omit<PersonalMetrics, 'id' | 'timestamp'>) =>
    apiClient.post<PersonalMetrics>('/v1/metrics', metrics),
  updateMetrics: (id: string, metrics: Partial<PersonalMetrics>) =>
    apiClient.put<PersonalMetrics>(`/v1/metrics/${id}`, metrics),
  deleteMetrics: (id: string) => apiClient.delete(`/v1/metrics/${id}`),
};

export const bodyMeasurementsApi = {
  getBodyMeasurements: () => apiClient.get<BodyMeasurement[]>('/v1/body-measurements'),
  addBodyMeasurement: (measurement: Omit<BodyMeasurement, 'id'>) =>
    apiClient.post<BodyMeasurement>('/v1/body-measurements', measurement),
  updateBodyMeasurement: (id: string, measurement: Partial<BodyMeasurement>) =>
    apiClient.put<BodyMeasurement>(`/v1/body-measurements/${id}`, measurement),
  deleteBodyMeasurement: (id: string) => apiClient.delete(`/v1/body-measurements/${id}`),
};

export const nutritionApi = {
  getNutritionEntries: () => apiClient.get<NutritionEntry[]>('/v1/nutrition'),
  addNutritionEntry: (entry: Omit<NutritionEntry, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiClient.post<NutritionEntry>('/v1/nutrition', entry),
  updateNutritionEntry: (id: string, entry: Partial<NutritionEntry>) =>
    apiClient.put<NutritionEntry>(`/v1/nutrition/${id}`, entry),
  deleteNutritionEntry: (id: string) => apiClient.delete(`/v1/nutrition/${id}`),
};

export const contactsApi = {
  getContacts: () => apiClient.get<Contact[]>('/v1/contacts'),
  getContact: (id: string) => apiClient.get<Contact>(`/v1/contacts/${id}`),
  createContact: (contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiClient.post<Contact>('/v1/contacts', contact),
  updateContact: (id: string, contact: Partial<Contact>) =>
    apiClient.put<Contact>(`/v1/contacts/${id}`, contact),
  deleteContact: (id: string) => apiClient.delete(`/v1/contacts/${id}`),
};

export const vehiclesApi = {
  getVehicles: () => apiClient.get<Vehicle[]>('/v1/vehicles'),
  getVehicle: (id: string) => apiClient.get<Vehicle>(`/v1/vehicles/${id}`),
  createVehicle: (vehicle: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiClient.post<Vehicle>('/v1/vehicles', vehicle),
  updateVehicle: (id: string, vehicle: Partial<Vehicle>) =>
    apiClient.put<Vehicle>(`/v1/vehicles/${id}`, vehicle),
  deleteVehicle: (id: string) => apiClient.delete(`/v1/vehicles/${id}`),
};

export const habitsApi = {
  getHabitDefinitions: () => apiClient.get<HabitDefinition[]>('/v1/habit-definitions'),
  createHabitDefinition: (habit: Omit<HabitDefinition, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiClient.post<HabitDefinition>('/v1/habit-definitions', habit),
  updateHabitDefinition: (id: string, habit: Partial<HabitDefinition>) =>
    apiClient.put<HabitDefinition>(`/v1/habit-definitions/${id}`, habit),
  deleteHabitDefinition: (id: string) => apiClient.delete(`/v1/habit-definitions/${id}`),
  
  getHabitEntries: (params?: { startDate?: string; endDate?: string; habitId?: string }) =>
    apiClient.get<HabitEntry[]>('/v1/habits', { params }),
  createHabitEntry: (entry: Omit<HabitEntry, 'id' | 'createdAt' | 'updatedAt'>) =>
    apiClient.post<HabitEntry>('/v1/habits', entry),
  updateHabitEntry: (id: string, entry: Partial<HabitEntry>) =>
    apiClient.put<HabitEntry>(`/v1/habits/${id}`, entry),
  deleteHabitEntry: (id: string) => apiClient.delete(`/v1/habits/${id}`),
};

export const bookmarksApi = {
  getBookmarks: (params?: { category?: string; tags?: string; search?: string }) =>
    apiClient.get<Bookmark[]>('/social/bookmarks', { params }),
  getBookmark: (id: string) => apiClient.get<Bookmark>(`/social/bookmarks/${id}`),
  createBookmark: (bookmark: Omit<Bookmark, 'id' | 'createdAt' | 'updatedAt' | 'visitCount' | 'lastVisited'>) =>
    apiClient.post<Bookmark>('/social/bookmarks', bookmark),
  updateBookmark: (id: string, bookmark: Partial<Bookmark>) =>
    apiClient.put<Bookmark>(`/social/bookmarks/${id}`, bookmark),
  deleteBookmark: (id: string) => apiClient.delete(`/social/bookmarks/${id}`),
  visitBookmark: (id: string) => apiClient.post(`/social/bookmarks/${id}/visit`),
};

export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  avatar?: string;
  role: 'user' | 'admin' | 'moderator';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  profile: {
    bio: string;
    location: string;
    timezone: string;
    language: string;
    theme: string;
  };
  preferences: {
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
    };
    privacy: {
      profileVisibility: 'public' | 'private';
      shareData: boolean;
    };
    dashboard: {
      defaultView: string;
      showStats: boolean;
      compactMode: boolean;
    };
  };
  security: {
    twoFactorEnabled: boolean;
    lastLogin?: string;
    loginCount: number;
    accountLocked: boolean;
  };
  metadata: {
    source: string;
    referrer?: string;
    tags: string[];
    notes: string;
  };
  createdAt: string;
  updatedAt: string;
  lastActiveAt: string;
}

export interface UserStats {
  total: number;
  active: number;
  admins: number;
  recentlyActive: number;
  inactive: number;
}

export const authApi = {
  signin: (credentials: { email: string; password: string }) =>
    apiClient.post('/auth/signin', credentials),
  signout: () => apiClient.post('/auth/logout'),
  getStatus: () => apiClient.get('/auth/status'),
};

export const usersApi = {
  getUsers: (params?: { status?: string; role?: string; search?: string }) =>
    apiClient.get<User[]>('/v1/users', { params }),
  getUser: (id: string) => apiClient.get<User>(`/v1/users/${id}`),
  getUserByUsername: (username: string) => apiClient.get<User>(`/v1/users/username/${username}`),
  getCurrentUser: () => apiClient.get<User>('/v1/users/current'),
  createUser: (user: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'lastActiveAt' | 'security'>) =>
    apiClient.post<User>('/v1/users', user),
  updateUser: (id: string, user: Partial<User>) =>
    apiClient.put<User>(`/v1/users/${id}`, user),
  deleteUser: (id: string) => apiClient.delete(`/v1/users/${id}`),
  updateUserActivity: (id: string) => apiClient.patch<User>(`/v1/users/${id}/activity`),
  updateUserPreferences: (id: string, preferences: User['preferences']) =>
    apiClient.put<User>(`/v1/users/${id}/preferences`, { preferences }),
  updateUserProfile: (id: string, profile: User['profile']) =>
    apiClient.put<User>(`/v1/users/${id}/profile`, { profile }),
  getUserStats: () => apiClient.get<UserStats>('/v1/users/stats'),
  getPublicProfile: (username: string) => apiClient.get<PublicProfile>(`/${username}`),
};

export const blogsApi = {
  // Blog Management
  getBlogs: (params?: { userId?: string }) =>
    apiClient.get<Blog[]>('/blogs', { params }),
  getBlogById: (id: string) => apiClient.get<Blog>(`/blogs/${id}`),
  getBlogBySlug: (slug: string, params?: { userId?: string }) =>
    apiClient.get<Blog>(`/blogs/slug/${slug}`, { params }),
  createBlog: (blog: Omit<Blog, 'id' | 'createdAt' | 'updatedAt' | 'slug'>) =>
    apiClient.post<Blog>('/blogs', blog),
  updateBlog: (id: string, blog: Partial<Blog>) =>
    apiClient.put<Blog>(`/blogs/${id}`, blog),
  deleteBlog: (id: string) => apiClient.delete(`/blogs/${id}`),
  
  // Blog Post Management
  getBlogPosts: (params?: { 
    blogId?: string; 
    userId?: string; 
    status?: string; 
    tags?: string; 
    page?: number; 
    limit?: number; 
  }) => apiClient.get<BlogPost[]>('/blog-posts', { params }),
  getBlogPostById: (id: string) => apiClient.get<BlogPost>(`/blog-posts/${id}`),
  getBlogPostBySlug: (slug: string, params?: { blogId?: string }) =>
    apiClient.get<BlogPost>(`/blog-posts/slug/${slug}`, { params }),
  createBlogPost: (post: {
    userId: string;
    blogId: string;
    title: string;
    content: string;
    status?: 'draft' | 'published' | 'scheduled' | 'archived';
    scheduledAt?: string | null;
    featuredImage?: string | null;
    tags?: string[];
    settings?: {
      allowComments?: boolean;
      isFeatured?: boolean;
      isPinned?: boolean;
    };
  }) => apiClient.post<BlogPost>('/blog-posts', post),
  updateBlogPost: (id: string, post: Partial<BlogPost>) =>
    apiClient.put<BlogPost>(`/blog-posts/${id}`, post),
  deleteBlogPost: (id: string) => apiClient.delete(`/blog-posts/${id}`),
  
  // Statistics
  getBlogStats: (params?: { userId?: string }) =>
    apiClient.get<BlogStats>('/blogs/stats', { params }),
};

export const preferencesApi = {
  // Site preferences (admin)
  getSitePreferences: () => apiClient.get<SitePreferences>('/v1/preferences/site'),
  updateSitePreferences: (preferences: Partial<SitePreferences>) =>
    apiClient.put<SitePreferences>('/v1/preferences/site', preferences),
  getSitePreferenceSection: (section: string) =>
    apiClient.get<any>(`/v1/preferences/site/${section}`),
  updateSitePreferenceSection: (section: string, updates: any) =>
    apiClient.put<any>(`/v1/preferences/site/${section}`, updates),

  // User preferences by ID (admin/user access)
  getUserPreferences: (userId: string) =>
    apiClient.get<UserPreferences>(`/v1/preferences/users/${userId}`),
  updateUserPreferences: (userId: string, preferences: Partial<UserPreferences>) =>
    apiClient.put<UserPreferences>(`/v1/preferences/users/${userId}`, preferences),
  getUserPreferenceSection: (userId: string, section: string) =>
    apiClient.get<any>(`/v1/preferences/users/${userId}/${section}`),
  updateUserPreferenceSection: (userId: string, section: string, updates: any) =>
    apiClient.put<any>(`/v1/preferences/users/${userId}/${section}`, updates),
  resetUserPreferences: (userId: string) =>
    apiClient.post<UserPreferences>(`/v1/preferences/users/${userId}/reset`),

  // Current user preferences (authenticated user)
  getCurrentUserPreferences: () => apiClient.get<UserPreferences>('/v1/preferences/me'),
  updateCurrentUserPreferences: (preferences: Partial<UserPreferences>) =>
    apiClient.put<UserPreferences>('/v1/preferences/me', preferences),

  // Utility endpoints
  getDefaultUserPreferences: () => apiClient.get<UserPreferences>('/v1/preferences/defaults'),
  getPreferencesSummary: (userId?: string) =>
    apiClient.get<PreferencesSummary>('/v1/preferences/summary', { params: userId ? { userId } : {} }),
};

export const infoApi = {
  getApiInfo: () => apiClient.get<{ name: string; version: string; status: string }>('/info'),
};